import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react'
import { ElementType, ReactNode } from 'react'
import { twJoin, twMerge } from 'tailwind-merge'
import { ArrowDownIcon } from '@systemeio/ui/icons'

interface DisclosureBoxProps {
  title: ((open: boolean) => ReactNode) | ReactNode
  subTitle?: ReactNode
  defaultOpen?: boolean
  buttonClassName?: string
  openButtonClassName?: string
  panelClassName?: string
  wrapperClassName?: string
  iconClassName?: string
  buttonAs?: ElementType
  disabled?: boolean
  children?: ReactNode | (() => ReactNode)
}
export function DisclosureBox({
  title,
  children,
  defaultOpen,
  buttonClassName,
  panelClassName,
  wrapperClassName,
  openButtonClassName,
  buttonAs,
  disabled,
  subTitle,
  iconClassName,
}: DisclosureBoxProps) {
  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div className={wrapperClassName}>
          <DisclosureButton
            disabled={disabled}
            as={buttonAs}
            className={twMerge(
              'bg-background-secondary ring-brand-primary flex w-full items-center justify-between gap-5 rounded-lg p-5 outline-hidden focus-visible:ring-2',
              open && 'border-text-secondary/30 rounded-b-none border-b',
              buttonClassName,
              open && openButtonClassName,
            )}
          >
            {typeof title === 'function' ? (
              title(open)
            ) : (
              <>
                {title}
                <div className={twMerge(iconClassName)}>
                  {subTitle}
                  <ArrowDownIcon
                    width={16}
                    height={16}
                    className={twJoin(
                      'stroke-text-primary shrink-0 transition-transform duration-300',
                      open && 'rotate-180',
                    )}
                  />
                </div>
              </>
            )}
          </DisclosureButton>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform opacity-0 -translate-y-2"
            enterTo="transform opacity-100 translate-y-0"
            leave="transition duration-75 ease-out"
            leaveFrom="transform opacity-100 translate-y-0"
            leaveTo="transform opacity-0 -translate-y-2"
          >
            <DisclosurePanel
              static
              className={twMerge(
                'bg-background-secondary rounded-lg rounded-t-none p-5',
                panelClassName,
              )}
            >
              {typeof children === 'function' ? children() : children}
            </DisclosurePanel>
          </Transition>
        </div>
      )}
    </Disclosure>
  )
}
